import type { VueConstructor, ComponentOptions } from 'vue';
import Vue from 'vue';
import { Select } from '@cognitoforms/element-ui';
import { Component, Watch } from 'vue-property-decorator';
import type { ElOption } from '@cognitoforms/element-ui/types/option';

const SelectConstructor = Vue.extend(Select) as VueConstructor<Select & SelectInternals>;

interface SelectInternals {
	visible: boolean;
	hoverIndex: number;
	filteredOptionsCount: number;
	optionsCount: number;
	options: Array<any>;
	selectedLabel: string;
	softFocus: boolean;
	renderOptions: boolean;
	automaticDropdown: boolean;
	menuVisibleOnFocus: boolean;
	debouncedQueryChange: (Event) => void;
	select: ComponentOptions<Select>;
	query: string;
	scrollToOption(option: any);
	selectOption: (e: unknown) => void;
}

@Component({})
export default class CSelectInput extends SelectConstructor {
	// -------------- Data --------------
	elValue: string = this.value;
	isFocused: boolean;
	orderedOptions: Array<any>;
	cachedQuery: string;

	// -------------- LifeCycle Hooks --------------
	mounted() {
		(this.$refs.reference as Vue).$on('input', this.handleReferenceInput);
		this.$el.addEventListener('keydown', this.handleKeyDown);
		// prevent mousedown on picker options from blurring the input and closing the picker
		this.popper.addEventListener('mousedown', e => e.preventDefault());
	}

	// -------------- Watchers --------------
	@Watch('value', { immediate: true })
	onValueChanged(valueProp) {
		this.elValue = valueProp;
	}

	@Watch('visible')
	onVisibleChanged() {
		if (this.visible) {
			document.addEventListener('mousemove', this.restorePointerEvent);
		}
		else {
			document.removeEventListener('mousemove', this.restorePointerEvent);
		}
	}

	@Watch('options')
	onOptionsChange() {
		if (this.cachedQuery)
			this.orderOptions(this.cachedQuery, true);
	}

	// -------------- Computed --------------
	get popper(): any {
		return (this.$refs.popper as Vue).$el;
	}

	get displayIndex(): number {
		const option = this.options[this.hoverIndex];
		return option ? Number(option.$el.style.order) : -1;
	}

	// The created option always appears at the top, but is technically at the last index of the list.
	// Therefore we must check this when determining whether or not arrow keys can navigate.
	get createdOptionIsHovered() {
		return this.options[this.hoverIndex] && this.options[this.hoverIndex].created === true;
	}

	get filteredOrderedOptionsCount(): number {
		return this.orderedOptions.filter(o => o.visible).length;
	}

	// -------------- Methods --------------
	restorePointerEvent() {
		this.popper.style.pointerEvents = 'auto';
	}

	removePointerEvent(popper) {
		popper.style.pointerEvents = 'none';
	}

	navigateOptions(direction) {
		const visibleOptions = this.orderedOptions.filter(o => o.visible);

		let index = this.displayIndex;
		if (direction === 'next') {
			index = this.displayIndex + 1;
			if (index >= visibleOptions.length)
				index = visibleOptions.length - 1;
		}
		else if (direction === 'prev') {
			index = this.displayIndex - 1;
			if (index < 0)
				index = 0;
		}
		const hoverOption = visibleOptions[index];
		const emptyOption = visibleOptions.find(o => !o.label);
		if (!hoverOption && emptyOption)
			emptyOption.hoverItem();
		else
			hoverOption.hoverItem();

		if (!this.multiple) {
			this.selectOption(hoverOption);
			this.scrollToOption(hoverOption);
			this.$nextTick(()=>	hoverOption.hoverItem());
		}
	}

	handleUpArrowKey(e) {
		e.preventDefault();

		if (!e.altKey && this.visible) {
			e.stopPropagation();
			e.preventDefault();

			this.removePointerEvent((this.$refs.popper as Vue).$el);

			// When you press the up arrow at the top of a select list, you should not go to the bottom.
			if (this.displayIndex === 0 || this.createdOptionIsHovered) {
				e.stopPropagation();
				e.preventDefault();
			}
			else
				this.navigateOptions('prev');
		}

		if (!e.altKey && (this.$parent as any).allowArrowsToChangeValue && !this.visible && !this.createdOptionIsHovered) {
			if (this.hoverIndex < this.filteredOrderedOptionsCount)
				this.hoverIndex--;

			if (this.hoverIndex >= 0) {
				this.selectedLabel = this.options[this.hoverIndex].value;
				this.$emit('change', this.selectedLabel);
			}
		}

		// ALT + UP: close the popover
		if (e.altKey) {
			e.preventDefault();
			this.visible = false;
		}
	}

	handleDownArrowKey(e) {
		e.preventDefault();

		if (!e.altKey && this.visible) {
			e.stopPropagation();
			e.preventDefault();

			this.removePointerEvent((this.$refs.popper as Vue).$el);

			// When you press the down arrow at the bottom of a select list, you should not go to the top.
			// Using this.orderedOptions.filter(o => o.visible).length instead of this.filteredOrderedOptionsCount here
			// because the latter was not being updated in time
			if (this.displayIndex < this.orderedOptions.filter(o => o.visible).length - 1 || this.createdOptionIsHovered) {
				this.navigateOptions('next');
			}
		}

		if (!e.altKey && (this.$parent as any).allowArrowsToChangeValue && !this.visible) {
			if (this.hoverIndex < 0)
				this.hoverIndex = 0;

			if (this.createdOptionIsHovered)
				this.hoverIndex = 0;
			else if (this.hoverIndex + 1 < this.filteredOrderedOptionsCount)
				this.hoverIndex++;

			this.selectedLabel = this.options[this.hoverIndex].value;
			this.$emit('change', this.selectedLabel);
		}

		// ALT + DOWN: open the popover
		if (e.altKey) {
			e.preventDefault();
			this.visible = true;
		}
	}

	// overrides a computed used by element internally
	get showNewOption() {
		const hasExistingOption = this.options.filter(option => !option.created)
			.some(option => option.currentValue === this.query);
		return this.filterable && this.allowCreate && this.query !== '' && this.query !== null && !hasExistingOption;
	}

	handleReferenceInput(val) {
		this.elValue = val;
	}

	handleFocus(event) {
		if (!this.softFocus)
			this.$emit('focus', event);
		else
			this.softFocus = false;
	}

	async handleKeyDown(e: KeyboardEvent) {
		// Exit if the key is any of the arrow keys, space, enter, tab, or shift
		// Or it's any "named" key (escape, home, etc)
		if (!e.key || this.visible || [37, 38, 39, 40, 32, 13, 9, 16].includes(e.keyCode) || e.key.length > 1)
			return null;

		e.preventDefault();

		this.visible = true;

		const optionsRendered = this.renderOptions;
		// Wait for the select to be 'visible'
		await this.$nextTick();
		// Wait for the select options to render
		if (!optionsRendered)
			await this.$nextTick();

		// Set the value of the input field to the key pressed
		this.selectedLabel = e.key;
		// Update the query to match the input field value
		this.debouncedQueryChange(e);
	}

	handleContainerClick(e) {
		if (!this.visible)
			Select.methods.handleContainerClick.call(this, e);
	}

	optionIsMatch(o) {
		if (this.allowCreate)
			return o.label === this.query;
		else
			return o.label.toLowerCase() === this.query.toLowerCase();
	}

	handleQueryChange(query) {
		Select.methods.handleQueryChange.call(this, query);
		this.cachedQuery = query;
		this.$nextTick(function () {
			this.orderOptions(query);
		});
	}

	handleBlur(e) {
		if (this.allowCreate)
			this.$emit('change', this.elValue);

		Select.methods.handleBlur.call(this, e);
		this.visible = false;
		this.$emit('blur', e);
	}

	findOption(value: string, includeHiddenOptions: boolean = false): ElOption | null {
		const scrollbar = this.$refs.scrollbar as Vue;
		const options = scrollbar.$children as ElOption[];
		const candidateOptions = includeHiddenOptions ? options : options.filter((o: (ElOption & { visible: boolean })) => o.visible);
		return candidateOptions.filter((o: ElOption) => o.value === value)[0] || null;
	}

	/**
	 * Similiar to Element's 'scrollToOption' method, except that instead of stopping as soon as the option is in view
	 * (such that it would be at the bottom of the list), scroll to the point where the option is at the top of the list.
	 * @param option The option to scroll to
	 */
	scrollToOptionAtTop(option: ElOption) {
		const scrollbar = this.$refs.scrollbar as Vue;
		const scrollbarWrapper = scrollbar.$refs.wrap as HTMLDivElement;
		scrollbarWrapper.scrollTo(0, (option.$el as HTMLLIElement).offsetTop);
	}

	orderOptions(query, ignoreSelected: boolean = false) {
		const shouldReorder = this.filterMatch === 'contains';
		const isEmptyQuery = !query || query === this.selected.label;
		// if empty query, restore original order
		if (!shouldReorder || (isEmptyQuery && !ignoreSelected)) {
			this.orderedOptions = this.options;
			for (let i = 0; i < this.options.length; i++) {
				this.options[i].$el.style.order = i;
			}
			if (query === this.selected.label) this.selected.hoverItem();

			return;
		}

		// Ordering options by 'contains' and then 'starts with'
		const sortFunction = (a, b) => {
			// created option should be first
			if (a.created) {
				return -1;
			}
			else if (b.created) {
				return 1;
			}
			else if (!a.label || !b.label) {
				return 0;
			}

			const exactAMatch = a.label.toLowerCase().startsWith(query.toLowerCase());
			const exactBMatch = b.label.toLowerCase().startsWith(query.toLowerCase());
			const looseAMatch = a.label.toLowerCase().includes(query.toLowerCase());
			const looseBMatch = b.label.toLowerCase().includes(query.toLowerCase());

			if (exactAMatch && exactBMatch) {
				return 0;
			}
			else if (exactAMatch) {
				return -1;
			}
			else if (exactBMatch) {
				return 1;
			}
			else if (looseAMatch && looseBMatch) {
				return 0;
			}
			else if (looseAMatch) {
				return -1;
			}
			else if (looseBMatch) {
				return 1;
			}
		};

		this.orderedOptions = this.options.slice(0).sort(sortFunction).filter(o => o.visible);
		for (let i = 0; i < this.orderedOptions.length; i++) {
			this.orderedOptions[i].$el.style.order = i;
		}
		if (!this.allowCreate)
			this.orderedOptions[0]?.hoverItem();
	}
}
